import React, { useEffect, useState } from "react";
import { guidance, daily, quotaApi } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { usePwaInstall } from "../context/PwaInstallContext";
import RiskBadge from "../components/RiskBadge";
import PaywallModal from "../components/PaywallModal";
import SaveToJournalModal from "../components/SaveToJournalModal";
import SaveWisdomButton from "../components/SaveWisdomButton";
import WisdomPathCard from "../components/WisdomPathCard";
import TrialBanner from "../components/TrialBanner";
import GuidanceResponse from "../components/GuidanceResponse";
import { toast } from "sonner";
import { Link, useNavigate } from "react-router-dom";
import {
  HeartHandshake,
  BookOpenText,
  Volume2,
  Pause,
  MessageSquareText,
  Scale,
  Sparkles,
  ChevronRight,
  Heart,
  ArrowRight,
  BookHeart,
  Gift,
} from "lucide-react";
import GiftModal from "../components/GiftModal";

const HERO_IMG =
  "https://images.unsplash.com/photo-1776074166962-aec94371d6d7?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjV8MHwxfHNlYXJjaHwxfHxwZWFjZWZ1bCUyMG1vcm5pbmclMjBuYXR1cmV8ZW58MHx8fHwxNzc3NTIwNjQ2fDA&ixlib=rb-4.1.0&q=85";

const SAMPLE_PROMPTS = [
  "I’m furious at my spouse and about to send a harsh text",
  "I just yelled at my kids and feel like a terrible parent",
  "I feel lost and don’t know what to do next",
  "I’m anxious about money and can’t sleep",
  "A friend betrayed my trust — should I cut them off?",
  "I feel like giving up on my marriage",
  "I’m tempted to do something I know I’ll regret",
  "I lost my temper at work today and snapped at someone",
];

function Section({ title, tone = "white", children }) {
  const styles = {
    sage: "bg-[#E1ECDD] border-[#d3e0d0]",
    cream: "bg-[#EEF3EC] border-[#dce5db]",
    white: "bg-white border-[#DCE5DB]",
    gold: "bg-[#F5EBD9] border-[#e8dcc4]",
    blue: "bg-[#EAF1F2] border-[#d6e2e4]",
  };
  return (
    <div className={`rounded-3xl border ${styles[tone]} p-6 shadow-[0_4px_12px_rgba(0,0,0,0.04)] fade-in-up`}>
      <h3 className="font-display text-lg text-[#2E2E2E] mb-2.5 tracking-tight">{title}</h3>
      <div className="text-[#2E2E2E]/90 text-[15px] leading-[1.75]">{children}</div>
    </div>
  );
}

function GuidanceResult({ data }) {
  const nextStepLines = (data.next_step || "")
    .split(/(?<=[.!?])\s+(?=[A-Z])/)
    .map((s) => s.trim())
    .filter(Boolean);

  return (
    <div data-testid="guidance-result" className="mt-6 space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <RiskBadge level={data.risk_level} />
        {data.will_escalate && (
          <span className="text-xs text-[#A86B4A] font-medium" data-testid="escalation-warning">
            Acting now may make this harder
          </span>
        )}
      </div>

      <Section title="What you’re feeling" tone="cream">
        <p>{data.feeling}</p>
      </Section>

      <Section title="Biblical perspective" tone="sage">
        <p className="italic text-[16px] leading-[1.85]">{data.biblical_perspective}</p>
        {data.scripture_reference && (
          <p className="mt-3 text-sm font-medium text-[#4F6F52]">— {data.scripture_reference}</p>
        )}
      </Section>

      <Section title="What you can do next" tone="white">
        {nextStepLines.length > 1 ? (
          <ul className="space-y-2">
            {nextStepLines.map((line, i) => (
              <li key={i} className="flex items-start gap-2.5">
                <span className="mt-2 w-1.5 h-1.5 rounded-full bg-[#4F6F52] shrink-0" />
                <span>{line}</span>
              </li>
            ))}
          </ul>
        ) : (
          <p>{data.next_step}</p>
        )}
      </Section>

      {data.better_response && (
        <Section title="A better way to respond" tone="gold">
          <p className="italic text-[15.5px]">“{data.better_response}”</p>
        </Section>
      )}

      {data.escalation_note && (
        <p className="text-xs text-[#6B6B6B] pt-1 leading-relaxed">{data.escalation_note}</p>
      )}
    </div>
  );
}

export default function HomePage() {
  const { user } = useAuth();
  const { maybeShowSaveToPhone } = usePwaInstall();
  const navigate = useNavigate();
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [followBusy, setFollowBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [resultInput, setResultInput] = useState("");
  const [saveJournal, setSaveJournal] = useState({ open: false, title: "", content: "" });
  const [giftOpen, setGiftOpen] = useState(false);
  const [today, setToday] = useState(null);
  const [todayLoading, setTodayLoading] = useState(true);
  const [speaking, setSpeaking] = useState(false);
  const [audioLoading, setAudioLoading] = useState(false);
  const [audioUrl, setAudioUrl] = useState(null);
  const audioRef = React.useRef(null);
  const [quota, setQuota] = useState(null);
  const [paywall, setPaywall] = useState({ open: false, reason: "" });

  useEffect(() => {
    daily
      .get()
      .then(setToday)
      .catch(() => {})
      .finally(() => setTodayLoading(false));
    quotaApi.get().then(setQuota).catch(() => {});
  }, []);

  const runGuidance = async (raw) => {
    const value = (raw ?? text).trim();
    if (!value) return;
    setBusy(true);
    setResult(null);
    try {
      const r = await guidance.send(value);
      setResult(r);
      setResultInput(value);
      setText("");
      quotaApi.get().then(setQuota).catch(() => {});
      // Meaningful first interaction — try to show Save to My Phone.
      // The provider itself gates to "once per visit" + "respect dismissals".
      try { maybeShowSaveToPhone("guidance_complete"); } catch {}
    } catch (err) {
      if (err?.response?.status === 402) {
        setPaywall({
          open: true,
          reason: "You've used your 1 free Guidance Moment for today. LifeGuide Pro is unlimited.",
        });
      } else {
        toast.error(err?.response?.data?.detail || "Let’s try that again");
      }
    } finally {
      setBusy(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    await runGuidance();
  };

  const pickPrompt = (s) => {
    setText(s);
    runGuidance(s);
  };

  const speak = async () => {
    if (!today) return;

    // If audio already loaded, toggle play/pause
    if (audioRef.current && audioUrl) {
      if (speaking) {
        audioRef.current.pause();
        setSpeaking(false);
      } else {
        audioRef.current.play();
        setSpeaking(true);
      }
      return;
    }

    setAudioLoading(true);
    try {
      const url = await daily.audio();
      setAudioUrl(url);
      const a = new Audio(url);
      audioRef.current = a;
      a.onended = () => setSpeaking(false);
      a.onerror = () => {
        setSpeaking(false);
        toast.error("Audio playback failed");
      };
      await a.play();
      setSpeaking(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn’t load the audio");
    } finally {
      setAudioLoading(false);
    }
  };

  return (
    <div className="space-y-10">
      {/* Header */}
      <div className="fade-in-up">
        <p className="text-sm text-[#6B6B6B]">
          {new Date().toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric" })}
        </p>
        <h1 className="font-display text-3xl sm:text-4xl text-[#2E2E2E] mt-1">
          Hello, {user?.name?.split(" ")[0] || "friend"}
        </h1>
        <p className="text-[#6B6B6B] mt-2">A quiet space to listen, reflect, and respond.</p>
      </div>

      <WisdomPathCard />

      <TrialBanner compact={true} />

      {/* Hero Crisis Input */}
      <form onSubmit={submit} className="fade-in-up stagger-1">
        <div
          data-testid="crisis-input-card"
          className="bg-white border border-[#DCE5DB] glow-focus rounded-3xl p-6 shadow-[0_4px_12px_rgba(0,0,0,0.04)] transition"
        >
          <label className="flex items-center gap-2 font-display text-lg sm:text-xl text-[#2E2E2E] mb-3">
            <span className="w-8 h-8 rounded-full bg-[#E1ECDD] text-[#4F6F52] flex items-center justify-center">
              <Heart size={15} strokeWidth={1.8} />
            </span>
            How are you feeling right now?
          </label>
          <textarea
            data-testid="crisis-input"
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="Type what you’re going through..."
            rows={3}
            className="w-full resize-none border-0 focus:outline-none focus:ring-0 text-[#2E2E2E] placeholder:text-[#a8a8a8] text-[15.5px] bg-transparent leading-relaxed"
          />
          <div className="flex items-center justify-between mt-2 gap-2 flex-wrap">
            <span className="text-xs text-[#6B6B6B]" data-testid="home-plan-strip">
              {quota
                ? quota.plan === "pro"
                  ? quota.trial_active
                    ? "Unlimited guidance during your Pro trial"
                    : "Pro · unlimited"
                  : `${Math.max(0, quota.guidance_limit - quota.guidance_used)} of ${quota.guidance_limit} guidance moments left today`
                : ""}
            </span>
            <button
              data-testid="crisis-submit"
              type="submit"
              disabled={busy || !text.trim()}
              className="bg-[#4F6F52] hover:bg-[#3F5A42] disabled:opacity-50 text-white rounded-full px-5 py-2.5 text-sm font-medium inline-flex items-center gap-2 transition shadow-[0_4px_14px_rgba(79,111,82,0.18)]"
            >
              {busy ? "Listening..." : "Get Guidance"} <ArrowRight size={14} />
            </button>
          </div>
        </div>

        {/* Sample prompts — real personal-crisis scenarios */}
        {!result && !busy && (
          <div className="mt-4 fade-in-up stagger-2" data-testid="sample-prompts">
            <div className="text-xs uppercase tracking-[0.16em] text-[#7FA7B0] font-semibold mb-2.5 px-1">
              Not sure where to start? Try one
            </div>
            <div className="flex flex-wrap gap-2">
              {SAMPLE_PROMPTS.map((s, i) => (
                <button
                  key={i}
                  type="button"
                  onClick={() => pickPrompt(s)}
                  data-testid={`sample-prompt-${i}`}
                  className="text-left text-sm bg-white border border-[#DCE5DB] hover:border-[#4F6F52]/40 hover:bg-[#EEF3EC] text-[#2E2E2E] rounded-full px-4 py-2 transition leading-relaxed"
                >
                  {s}
                </button>
              ))}
            </div>
          </div>
        )}
      </form>

      {busy && (
        <div className="space-y-3" data-testid="guidance-loading">
          <div className="h-12 w-40 rounded-full shimmer" />
          <div className="h-28 rounded-3xl shimmer" />
          <div className="h-28 rounded-3xl shimmer" />
        </div>
      )}

      {result && (
        <div className="space-y-3">
          {result.what_im_hearing || result.work_through?.length || result.follow_up_question ? (
            <GuidanceResponse
              result={result}
              busy={followBusy}
              onFollowUp={async (t) => {
                if (!t?.trim() || followBusy) return;
                setFollowBusy(true);
                try {
                  const history = [
                    { role: "user", content: resultInput || "" },
                    {
                      role: "assistant",
                      content: result.follow_up_question || result.next_step || "",
                    },
                  ];
                  const r = await guidance.send(t.trim(), history);
                  setResult(r);
                  setResultInput(t.trim());
                  quotaApi.get().then(setQuota).catch(() => {});
                } catch (err) {
                  if (err?.response?.status === 402) {
                    setPaywall({
                      open: true,
                      reason:
                        "You've used your Guidance for today. LifeGuide Pro is unlimited.",
                    });
                  } else {
                    toast.error(err?.response?.data?.detail || "Let's try that again");
                  }
                } finally {
                  setFollowBusy(false);
                }
              }}
              onAction={(a) => {
                if (a === "say_it_better") {
                  const q = new URLSearchParams({
                    message: result.say_it_better_prefill?.draft || result.better_response || "",
                    prior: result.say_it_better_prefill?.original || resultInput || "",
                  });
                  navigate(`/analyze?${q.toString()}`);
                  return;
                }
                if (a === "journal_this") {
                  const q = (resultInput || "").trim();
                  const parts = [];
                  if (q) parts.push(`What I shared:\n${q}`);
                  if (result.what_im_hearing || result.feeling)
                    parts.push(
                      `What I'm hearing:\n${result.what_im_hearing || result.feeling}`
                    );
                  if (result.wisdom_for_moment || result.biblical_perspective)
                    parts.push(
                      `Wisdom for this moment:\n${
                        result.wisdom_for_moment || result.biblical_perspective
                      }${result.scripture_reference ? `\n— ${result.scripture_reference}` : ""}`
                    );
                  if (result.next_step) parts.push(`Next step:\n${result.next_step}`);
                  setSaveJournal({
                    open: true,
                    title: q ? q.slice(0, 80) : "Guidance moment",
                    content: parts.join("\n\n"),
                  });
                  return;
                }
                if (a === "save_wisdom") {
                  document.querySelector('[data-testid="guidance-save-wisdom"]')?.click();
                  return;
                }
                if (a === "continue_talking" || a === "make_plan") {
                  navigate("/coach");
                  return;
                }
              }}
              testidPrefix="guidance"
            />
          ) : (
            <GuidanceResult data={result} />
          )}
          <div className="flex flex-wrap justify-end gap-2">
            <SaveWisdomButton
              kind="guidance"
              referenceId={result.id || `guidance-${resultInput?.slice(0, 40) || Date.now()}`}
              title={resultInput ? resultInput.slice(0, 100) : "Guidance moment"}
              body={[
                result.feeling ? `Feeling: ${result.feeling}` : null,
                result.biblical_perspective
                  ? `Biblical perspective: ${result.biblical_perspective}${
                      result.scripture_reference ? ` — ${result.scripture_reference}` : ""
                    }`
                  : null,
                result.next_step ? `Next step: ${result.next_step}` : null,
              ]
                .filter(Boolean)
                .join("\n\n")}
              meta={{ scripture_reference: result.scripture_reference || null }}
              testid="guidance-save-wisdom"
            />
            <button
              data-testid="guidance-save-to-journal"
              onClick={() => {
                const q = (resultInput || "").trim();
                const parts = [];
                if (q) parts.push(`What I shared:\n${q}`);
                if (result.feeling) parts.push(`Feeling:\n${result.feeling}`);
                if (result.biblical_perspective)
                  parts.push(
                    `Biblical perspective:\n${result.biblical_perspective}${
                      result.scripture_reference ? `\n— ${result.scripture_reference}` : ""
                    }`,
                  );
                if (result.next_step) parts.push(`Next step:\n${result.next_step}`);
                setSaveJournal({
                  open: true,
                  title: q ? q.slice(0, 80) : "Guidance moment",
                  content: parts.join("\n\n"),
                });
              }}
              className="inline-flex items-center gap-1.5 text-sm font-medium text-[#3F5A42] hover:text-[#2E4531] bg-[#E1ECDD] hover:bg-[#D0DFCB] border border-[#B7CCB1] hover:border-[#4F6F52]/60 rounded-full px-4 py-2 transition shadow-[0_2px_8px_rgba(79,111,82,0.10)]"
            >
              <BookHeart size={14} strokeWidth={2.2} /> Save to my Journal
            </button>
          </div>
        </div>
      )}

      {/* Daily Devotional */}
      <section className="fade-in-up stagger-2">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-display text-xl text-[#2E2E2E]">Today’s guidance</h2>
        </div>
        {todayLoading ? (
          <div className="rounded-3xl h-56 shimmer" />
        ) : today ? (
          <article
            data-testid="daily-devotional-card"
            className="rounded-3xl overflow-hidden border border-[#DCE5DB] bg-[#EEF3EC] shadow-[0_4px_12px_rgba(0,0,0,0.04)]"
          >
            <div className="relative h-36 sm:h-44">
              <img src={HERO_IMG} alt="morning" className="absolute inset-0 w-full h-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#2e2e2e]/60 via-[#2e2e2e]/15 to-transparent" />
              <div className="absolute bottom-0 left-0 p-6">
                <h3 className="font-display text-2xl sm:text-3xl text-white drop-shadow">{today.title}</h3>
              </div>
            </div>
            <div className="p-6 space-y-5">
              <p className="text-[15.5px] text-[#2E2E2E] leading-[1.8]">{today.message}</p>
              <blockquote className="border-l-2 border-[#4F6F52] pl-4 text-[#2E2E2E] italic text-[16px] leading-[1.85]">
                “{today.scripture}”
                <div className="not-italic text-sm text-[#6B6B6B] mt-2 font-normal">{today.scripture_reference}</div>
              </blockquote>
              <div className="rounded-2xl bg-white border border-[#DCE5DB] p-5">
                <div className="text-xs uppercase tracking-[0.16em] text-[#7FA7B0] font-semibold mb-1.5">A moment to reflect</div>
                <div>{today.reflection_question}</div>
              </div>
              <div className="rounded-2xl bg-[#E1ECDD] p-5">
                <div className="text-xs uppercase tracking-[0.16em] text-[#4F6F52] font-semibold mb-1.5">Today’s small step</div>
                <div className="text-[#2E2E2E]">{today.action_step}</div>
              </div>
              <div className="flex gap-3 pt-1">
                <button
                  data-testid="devotional-listen"
                  onClick={speak}
                  disabled={audioLoading}
                  className="inline-flex items-center gap-2 bg-white border border-[#DCE5DB] hover:bg-[#EEF3EC] hover:border-[#4F6F52]/30 rounded-full px-5 py-2.5 text-sm transition disabled:opacity-60"
                >
                  {audioLoading ? (
                    <>
                      <span className="w-3.5 h-3.5 border-2 border-[#4F6F52] border-t-transparent rounded-full animate-spin" />
                      Loading...
                    </>
                  ) : speaking ? (
                    <>
                      <Pause size={14} /> Pause
                    </>
                  ) : (
                    <>
                      <Volume2 size={14} /> Listen
                    </>
                  )}
                </button>
                <SaveWisdomButton
                  kind="devotional"
                  referenceId={today.date || today.id || today.title}
                  title={today.title}
                  body={`${today.message}\n\n"${today.scripture}"${today.scripture_reference ? ` — ${today.scripture_reference}` : ""}\n\nReflect: ${today.reflection_question}\n\nToday's small step: ${today.action_step}`}
                  meta={{ scripture_reference: today.scripture_reference, date: today.date || null }}
                  testid="devotional-save-wisdom"
                />
              </div>
            </div>
          </article>
        ) : (
          <div className="text-[#6B6B6B] text-sm">Today’s guidance is loading. Try again in a moment.</div>
        )}
      </section>

      {/* Quick actions */}
      <section className="fade-in-up stagger-3">
        <h2 className="font-display text-xl text-[#2E2E2E] mb-4">Gentle next steps</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <QuickAction to="/coach" label="Talk it through" desc="A patient, listening voice" icon={MessageSquareText} testid="quick-coach" />
          <QuickAction to="/analyze" label="Say It Better" desc="Paste your text. Get a kinder, clearer way to say it." icon={HeartHandshake} testid="quick-analyze" />
          <QuickAction to="/bible" label="Sit with scripture" desc="Meaning · context · today" icon={BookOpenText} testid="quick-bible" />
          <QuickAction to="/decision" label="Weigh a decision" desc="Wisdom for crossroads" icon={Scale} testid="quick-decision" />
          <QuickAction to="/growth" label="Daily check-in" desc="A small moment of honesty" icon={Sparkles} testid="quick-checkin" />
          <QuickAction to="/journal" label="Journal" desc="Write, reflect, and remember" icon={BookHeart} testid="quick-journal" />
        </div>
      </section>

      {/* See Plans & Pricing — reuses the existing PaywallModal so pricing
          logic lives in exactly one place. Visible above the Gift card. */}
      <section
        data-testid="home-plans-section"
        className="fade-in-up stagger-4"
      >
        <button
          onClick={() =>
            setPaywall({
              open: true,
              reason:
                "Unlimited guidance, full history, journal, reflections, and more.",
            })
          }
          data-testid="home-plans-cta"
          className="group w-full text-left rounded-3xl border border-[#B7CCB1] bg-gradient-to-br from-[#EEF3EC] to-[#E1ECDD] p-5 shadow-[0_4px_14px_rgba(79,111,82,0.10)] hover:shadow-[0_6px_18px_rgba(79,111,82,0.16)] transition"
        >
          <div className="flex items-center gap-4">
            <span className="w-12 h-12 shrink-0 rounded-2xl bg-[#4F6F52] text-white flex items-center justify-center shadow-[0_4px_10px_rgba(79,111,82,0.25)]">
              <Sparkles size={19} strokeWidth={2.2} />
            </span>
            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between gap-2">
                <h3 className="font-display text-lg text-[#2E2E2E]">LifeGuide Pro</h3>
                <span
                  data-testid="home-plans-cta-label"
                  className="hidden sm:inline-flex items-center gap-1 text-sm font-semibold text-[#3F5A42] group-hover:text-[#2E4531] transition"
                >
                  See Plans &amp; Pricing
                  <ArrowRight
                    size={14}
                    className="shrink-0 group-hover:translate-x-0.5 transition"
                  />
                </span>
              </div>
              <p className="text-sm text-[#3F5A42] mt-1 leading-relaxed">
                Unlimited guidance, full history, journal, reflections, and more.
              </p>
              <span className="mt-2 sm:hidden inline-flex items-center gap-1 text-sm font-semibold text-[#3F5A42]">
                See Plans &amp; Pricing
                <ArrowRight size={14} className="shrink-0" />
              </span>
            </div>
          </div>
        </button>
      </section>

      {/* Give a Gift — visible entry point, opens the existing gift flow */}
      <section
        data-testid="home-gift-section"
        className="fade-in-up stagger-4"
      >
        <button
          onClick={() => setGiftOpen(true)}
          data-testid="home-gift-cta"
          className="group w-full text-left rounded-3xl border border-[#D6C7A1]/70 bg-gradient-to-br from-[#FBF3E1] to-[#F5EBD9] p-5 shadow-[0_4px_14px_rgba(140,122,79,0.10)] hover:shadow-[0_6px_18px_rgba(140,122,79,0.16)] transition"
        >
          <div className="flex items-center gap-4">
            <span className="w-12 h-12 shrink-0 rounded-2xl bg-[#8C7A4F] text-white flex items-center justify-center shadow-[0_4px_10px_rgba(140,122,79,0.25)]">
              <Gift size={19} strokeWidth={2.2} />
            </span>
            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between gap-2">
                <h3 className="font-display text-lg text-[#2E2E2E]">Give LifeGuide as a Gift</h3>
                <ArrowRight
                  size={16}
                  className="text-[#8C7A4F] shrink-0 group-hover:translate-x-0.5 transition"
                />
              </div>
              <p className="text-sm text-[#5A4F35] mt-1 leading-relaxed">
                Send 1, 3, 6, or 12 months — anonymously or from you.
              </p>
            </div>
          </div>
        </button>
      </section>
      <PaywallModal
        open={paywall.open}
        reason={paywall.reason}
        onClose={() => setPaywall({ open: false, reason: "" })}
      />
      <SaveToJournalModal
        open={saveJournal.open}
        initialTitle={saveJournal.title}
        initialContent={saveJournal.content}
        source="guidance"
        sourceRef={result?.id || null}
        onClose={() => setSaveJournal({ open: false, title: "", content: "" })}
        onLockedWrite={() =>
          setPaywall({
            open: true,
            reason: "Your LifeGuide journey is saved. Upgrade to save entries to your Journal.",
          })
        }
      />
      <GiftModal open={giftOpen} onClose={() => setGiftOpen(false)} />
    </div>
  );
}

function QuickAction({ to, label, desc, icon: Icon, testid }) {
  return (
    <Link
      to={to}
      data-testid={testid}
      className="group flex items-center justify-between bg-white border border-[#DCE5DB] hover:border-[#4F6F52]/40 hover:shadow-[0_4px_14px_rgba(79,111,82,0.08)] rounded-2xl p-4 transition"
    >
      <div className="flex items-center gap-3.5">
        <div className="w-11 h-11 rounded-2xl bg-[#E1ECDD] text-[#4F6F52] flex items-center justify-center group-hover:bg-[#4F6F52] group-hover:text-white transition">
          <Icon size={18} strokeWidth={1.6} />
        </div>
        <div>
          <div className="font-medium text-[#2E2E2E]">{label}</div>
          <div className="text-xs text-[#6B6B6B] mt-0.5">{desc}</div>
        </div>
      </div>
      <ChevronRight size={16} className="text-[#6B6B6B] group-hover:text-[#4F6F52] transition" />
    </Link>
  );
}
